import React from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, {Defs, LinearGradient, Rect, Stop} from 'react-native-svg';
import {colors, fonts} from '../config/theme';

// The app's one full-width button: 56pt tall, 16pt corners. Every
// full-width action uses it (PrimaryButton wraps it) so they all match.
// variant: 'light' (white, outlined — Google)
//          | 'dark' (ink fill — Apple)
//          | 'gradient' (emerald gradient — the Login CTA)
//          | 'primary' (solid forest — form submit buttons)
//          | 'danger' (error fill — destructive confirms)
// icon / iconRight: optional elements laid out inline around the label.
// loading: swaps the content for a spinner and blocks presses.
//
// Shadows are iOS-only: RN 0.77 Android draws near-square elevation
// outlines under non-pill radii (see the note in config/theme.js).
const AuthButton = ({
  title,
  onPress,
  icon,
  iconRight,
  variant = 'light',
  loading = false,
  disabled = false,
  style,
}) => {
  const isLight = variant === 'light';
  const isGradient = variant === 'gradient';
  const isPrimary = variant === 'primary' || variant === 'danger';
  const inactive = disabled || loading;
  return (
    <View
      style={[styles.shadow, shadowStyles[variant], style]}>
      <Pressable
        onPress={onPress}
        disabled={inactive}
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{disabled: inactive, busy: loading}}
        style={({pressed}) => [
          styles.button,
          styles[variant],
          pressed && styles[`${variant}Pressed`],
          pressed && styles.pressed,
          disabled && styles.disabled,
        ]}>
        {isGradient ? <GradientFill /> : null}
        {loading ? (
          <ActivityIndicator
            color={isLight ? colors.brandDeep : colors.onPrimary}
          />
        ) : (
          <View
            style={[
              styles.content,
              (isGradient || isPrimary) && styles.contentTight,
            ]}>
            {icon}
            <Text
              style={[
                styles.label,
                isLight ? styles.labelOnLight : styles.labelOnDark,
                isGradient && styles.labelStrong,
                isPrimary && styles.labelBold,
              ]}>
              {title}
            </Text>
            {iconRight}
          </View>
        )}
      </Pressable>
    </View>
  );
};

const GradientFill = () => (
  <Svg style={StyleSheet.absoluteFill} pointerEvents="none">
    <Defs>
      <LinearGradient id="authCta" x1="0" y1="0" x2="1" y2="0">
        <Stop offset="0" stopColor={colors.brandDeep} />
        <Stop offset="0.5" stopColor={colors.brandMid} />
        <Stop offset="1" stopColor={colors.brandBright} />
      </LinearGradient>
    </Defs>
    <Rect width="100%" height="100%" fill="url(#authCta)" />
  </Svg>
);

const RADIUS = 16;

const softShadow = Platform.select({
  ios: {
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.05,
    shadowRadius: 4,
  },
  default: {},
});

const shadowStyles = {
  light: softShadow,
  dark: softShadow,
  primary: softShadow,
  danger: softShadow,
  gradient: Platform.select({
    ios: {
      shadowColor: colors.brandBright,
      shadowOffset: {width: 0, height: 10},
      shadowOpacity: 0.35,
      shadowRadius: 12,
    },
    default: {},
  }),
};

const styles = StyleSheet.create({
  shadow: {
    width: '100%',
    borderRadius: RADIUS,
  },
  button: {
    height: 56,
    borderRadius: RADIUS,
    overflow: 'hidden',
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  contentTight: {
    gap: 8,
  },
  light: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  lightPressed: {
    backgroundColor: '#F8FAFC',
  },
  dark: {
    backgroundColor: colors.ink,
  },
  darkPressed: {
    backgroundColor: colors.inkPressed,
  },
  primary: {
    backgroundColor: colors.primary,
  },
  primaryPressed: {
    backgroundColor: colors.primaryPressed,
  },
  danger: {
    backgroundColor: colors.error,
  },
  dangerPressed: {
    backgroundColor: colors.errorPressed,
  },
  gradient: {},
  gradientPressed: {
    opacity: 0.92,
  },
  pressed: {
    transform: [{scale: 0.98}],
  },
  disabled: {
    opacity: 0.5,
  },
  label: {
    fontFamily: fonts.medium,
    fontSize: 15,
    lineHeight: 20,
  },
  labelOnLight: {
    color: colors.textStrong,
  },
  labelOnDark: {
    color: colors.onPrimary,
  },
  labelStrong: {
    fontFamily: fonts.semiBold,
  },
  labelBold: {
    fontFamily: fonts.bold,
  },
});

export default AuthButton;
